import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Clock, AlertCircle, FileText, Trash2, LayoutTemplate } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { formatDate } from '../../utils/formatters';
import { config } from '../../utils/config';
import { apiClient } from '../../api/client';
import InvoicesSubNav from '../InvoicesSubNav';
import { useSubscription } from '../../hooks/useSubscription';
import '../../styles/invoices.css';

interface Template {
  id: string;
  name: string;
  description: string;
  fieldCount: number;
  lastUsed: string;
  createdAt: string;
}

const EXPLAINER =
  "Add a sample invoice from a supplier you get bills from often. We'll learn where they put each detail, so future invoices from them are read more accurately.";

const TemplatePage = () => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const templateLimit = subscription?.templates_limit ?? 3;
  const atLimit = !loading && templates.length >= templateLimit;

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(config.endpoints.templates);
      setTemplates(response.data);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Could not load your templates'));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => navigate('/uploaded-invoices/templates/new');

  const handleOpen = (id: string) => navigate(`/uploaded-invoices/templates/${id}`);

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deleteConfirm === id) {
      try {
        setDeletingId(id);
        await apiClient.delete(`${config.endpoints.templates}/${id}`);
        setTemplates(templates.filter(t => t.id !== id));
        setDeleteConfirm(null);
      } catch (err: unknown) {
        setError(getErrorMessage(err, 'Could not delete this template'));
      } finally {
        setDeletingId(null);
      }
    } else {
      setDeleteConfirm(id);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const renderCard = (template: Template) => (
    <Card
      key={template.id}
      className="hover:bg-coral-500/10 transition-all duration-200 cursor-pointer"
      onClick={() => handleOpen(template.id)}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-coral-500/10">
              <FileText className="w-6 h-6 text-coral-400" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base text-foreground truncate">{template.name}</CardTitle>
              <p className="text-sm text-foreground-muted">
                {template.fieldCount} {template.fieldCount === 1 ? 'detail' : 'details'} learned
              </p>
            </div>
          </div>
          <button
            onClick={(e) => handleDelete(template.id, e)}
            disabled={deletingId === template.id}
            className={`p-1 rounded transition-colors ${
              deletingId === template.id
                ? 'text-foreground-muted bg-coral-500/10 cursor-wait'
                : deleteConfirm === template.id
                ? 'text-red-400 bg-red-500/20'
                : 'text-coral-400 hover:text-red-400 hover:bg-red-500/20'
            }`}
            title={
              deletingId === template.id
                ? 'Removing…'
                : deleteConfirm === template.id
                ? 'Click again to remove'
                : 'Remove template'
            }
          >
            {deletingId === template.id ? (
              <div className="w-4 h-4 border-2 border-coral-400/20 border-t-coral-400 rounded-full animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 text-foreground-muted">
          <Clock className="w-4 h-4" />
          <span className="text-xs">Last used {formatDate(template.lastUsed)}</span>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="inv-container">
      <InvoicesSubNav />

      {/* Header */}
      <div className="inv-header" style={{ marginTop: 24 }}>
        <div>
          <div className="inv-eyebrow"><span className="dot" />Smarter reading for regular suppliers</div>
          <h1>Templates</h1>
          <p className="sub" style={{ maxWidth: 640 }}>{EXPLAINER}</p>
        </div>
        <div className="inv-header-actions" style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <button
            onClick={handleAdd}
            disabled={atLimit}
            className="inv-btn primary"
            title={atLimit ? `You've reached your ${templateLimit}-template limit` : 'Add a template'}
          >
            <Plus />
            Add template
          </button>
          {!loading && (
            <span className="text-xs text-foreground-muted">
              {templates.length} of {templateLimit} used
            </span>
          )}
        </div>
      </div>

      {/* At-limit notice */}
      {atLimit && (
        <div className="inv-panel" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle className="w-4 h-4 shrink-0 text-foreground-muted" />
          <p className="text-sm text-foreground-secondary">
            You've reached your template limit ({templateLimit}). Delete one to add another
            {subscription?.plan_type !== 'advanced' && (
              <>
                {' '}or{' '}
                <button onClick={() => navigate('/pricing')} className="text-coral-600 dark:text-coral-300 underline">
                  upgrade your plan
                </button>
              </>
            )}
            .
          </p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="inv-loading">
          <div className="inv-spinner" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="inv-alert">
          <AlertCircle />
          <p>{error}</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && templates.length === 0 && (
        <div className="inv-empty">
          <div className="inv-empty-icon">
            <LayoutTemplate />
          </div>
          <h3>No templates yet</h3>
          <p style={{ maxWidth: 420, margin: '0 auto 16px' }}>{EXPLAINER}</p>
          <button onClick={handleAdd} className="inv-btn primary">
            <Plus />
            Add your first template
          </button>
        </div>
      )}

      {/* Grid */}
      {!loading && !error && templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(renderCard)}
        </div>
      )}
    </div>
  );
};

function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    if ('response' in error && error.response && typeof error.response === 'object') {
      const axiosError = error as { response: { data?: { detail?: string } } };
      return axiosError.response.data?.detail || fallback;
    }
    if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
      return (error as { message: string }).message;
    }
  }
  return fallback;
}

export default TemplatePage;
